import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Preloader from "../../shared/PreLoaders";
import { Authorization } from "../../Context/Authorization";
import { AuthContext } from "../../Context/AuthContext";

/* ── Shared style tokens ───────────────────────────────────── */
const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0",
  borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff",
  outline: "none", transition: "border-color 0.2s",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "block" };
const errorStyle = { fontSize: 11, color: "#C62828", marginTop: 4 };
const readOnlyStyle = { ...inputStyle, background: "#F7FAFC", color: "#718096", cursor: "not-allowed" };

const Field = ({ label, error, children }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={labelStyle}>{label}</label>
    {children}
    {error && <div style={errorStyle}>{error}</div>}
  </div>
);

const Input = ({ error, readOnly, ...props }) => (
  <input
    style={readOnly ? readOnlyStyle : { ...inputStyle, borderColor: error ? "#E53E3E" : "#E2E8F0" }}
    onFocus={(e) => { if (!readOnly) e.target.style.borderColor = "#4CAF50"; }}
    onBlur={(e) => { if (!readOnly) e.target.style.borderColor = error ? "#E53E3E" : "#E2E8F0"; }}
    readOnly={readOnly}
    {...props}
  />
);

const Select = ({ error, children, ...props }) => (
  <select
    style={{ ...inputStyle, borderColor: error ? "#E53E3E" : "#E2E8F0", cursor: "pointer" }}
    onFocus={(e) => e.target.style.borderColor = "#4CAF50"}
    onBlur={(e) => e.target.style.borderColor = error ? "#E53E3E" : "#E2E8F0"}
    {...props}
  >
    {children}
  </select>
);

const Section = ({ icon, title, children, cols = 2 }) => (
  <div style={{ marginBottom: 24 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid #EDF2F7" }}>
      <span style={{ width: 28, height: 28, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, flexShrink: 0 }}>
        <i className={`mdi ${icon}`} />
      </span>
      <span style={{ fontSize: 13, fontWeight: 700, color: "#2D3748" }}>{title}</span>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))`, gap: "0 20px" }}>
      {children}
    </div>
  </div>
);

/* ── AddVFC ─────────────────────────────────────────────────── */
const AddVfc = () => {
  const { selectedJfmcType } = useContext(AuthContext);
  const Api = process.env.REACT_APP_API;

  const initialStage = {
    vfcName: "", rangeId: "", district: "", beatName: "", lgdCode: "",
    beatCode: "", bdoOfficeName: "", bdoOfficeCode: "", location: "",
    address: "", villageName: "", villageCode: "", panchayath: "",
    thaluk: "", thalukCode: "", pinCode: "", dateOfEstablishment: "",
    bankName: "", branch: "", accountNumber: "", ifscCode: "",
    registrationNo: "", registrationDate: "", totalFamily: "", totalPopulation: "",
  };

  const [formData, setFormData]       = useState(initialStage);
  const [errors, setErrors]           = useState({});
  const [loading, setLoading]         = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [districts, setDistricts]     = useState([]);
  const [blocks, setBlocks]           = useState([]);
  const [villages, setVillages]       = useState([]);
  const [rangeOptions, setRangeOptions] = useState([]);
  const [beatOptions, setBeatOptions]   = useState([]);

  const validateForm = (d) => {
    const e = {};
    if (!d.vfcName)             e.vfcName = "JFMC name is required";
    if (!d.rangeId)             e.rangeId = "Please select Range";
    if (!d.location)            e.location = "Please enter Location";
    if (!d.villageName)         e.villageName = "Please enter Village Name";
    if (!d.district)            e.district = "Please select District";
    if (!d.registrationNo)      e.registrationNo = "Please enter Registration Number";
    if (!d.registrationDate)    e.registrationDate = "Please enter Registration Date";
    if (!d.totalFamily)         e.totalFamily = "Please enter Total Family";
    if (!d.totalPopulation)     e.totalPopulation = "Please enter Total Population";
    if (!d.panchayath)          e.panchayath = "Please enter Panchayath";
    if (!d.thaluk)              e.thaluk = "Please enter Thaluk";
    if (!d.thalukCode)          e.thalukCode = "Please enter Thaluk Code";
    if (!d.pinCode)             e.pinCode = "Please enter Pincode";
    if (!d.dateOfEstablishment) e.dateOfEstablishment = "Please enter Date of Establishment";
    if (!d.bankName)            e.bankName = "Please enter Bank Name";
    if (!d.branch)              e.branch = "Please enter Branch";
    if (!d.accountNumber)       e.accountNumber = "Please enter Account Number";
    if (!d.ifscCode)            e.ifscCode = "Please enter IFSC Code";
    return e;
  };

  useEffect(() => {
    setLoading(true);
    const fetchDistricts = async () => {
      try {
        const Config = Authorization();
        const res = await axios.get(`${Api}/api/lgd/districts`, Config);
        setDistricts(res.data);
      } catch (err) { console.error("Failed to fetch districts", err); }
    };
    const fetchRange = async () => {
      try {
        const Config = Authorization();
        const res = await axios.get(`${Api}/api/ranges`, Config);
        if (Array.isArray(res.data)) setRangeOptions(res.data);
      } catch (err) { console.error("Error fetching ranges", err); }
      finally { setLoading(false); }
    };
    fetchDistricts();
    fetchRange();
  }, [Api]);

  useEffect(() => {
    if (formData.rangeId) fetchBeatsForRange(formData.rangeId);
    else setBeatOptions([]);
  }, [formData.rangeId]);

  const fetchBeatsForRange = async (rangeId) => {
    try {
      const Config = Authorization();
      const res = await axios.get(`${Api}/api/beat/by-range/${rangeId}`, Config);
      setBeatOptions(res.data.beats);
    } catch (err) { console.error("Error fetching beats", err); }
  };

  const fetchBlocks = async (districtCode) => {
    try {
      const config = Authorization();
      const res = await axios.get(`${Api}/api/lgd/blocks?districtCode=${districtCode}`, config);
      setBlocks(Array.isArray(res.data.blocks) ? res.data.blocks : []);
    } catch (err) { console.error("Error fetching blocks", err); setBlocks([]); }
  };

  const fetchVillages = async (blockCode) => {
    try {
      const config = Authorization();
      const res = await axios.get(`${Api}/api/lgd/villages?districtCode=${formData.district}&blockCode=${blockCode}`, config);
      setVillages(Array.isArray(res.data) ? res.data : []);
    } catch (err) { console.error("Error fetching villages", err); setVillages([]); }
  };

  const fetchPinCode = async (villageName) => {
    try {
      if (!villageName) return;
      const cleanedName = villageName.trim().replace(/\s+/g, " ");
      const response = await axios.get(`https://api.postalpincode.in/postoffice/${encodeURIComponent(cleanedName)}`);
      const postData = response.data?.[0];
      if (postData?.Status === "Success" && postData?.PostOffice?.length > 0) {
        setFormData((prev) => ({ ...prev, pinCode: postData.PostOffice[0].Pincode }));
      } else {
        setFormData((prev) => ({ ...prev, pinCode: "" }));
      }
    } catch (err) { console.error("Error fetching pincode", err); }
  };

  const handleRangeChange = async (e) => {
    const selectedRangeId = e.target.value;
    setFormData((prev) => ({ ...prev, rangeId: selectedRangeId, beatName: "", beatCode: "" }));
    if (selectedRangeId) await fetchBeatsForRange(selectedRangeId);
  };

  const handleBeatNameChange = (e) => {
    const selectedBeatName = e.target.value;
    const beat = beatOptions.find((b) => b.beatName === selectedBeatName);
    setFormData((prev) => ({ ...prev, beatName: selectedBeatName, beatCode: beat ? beat.beatCode : "" }));
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    const updated = { ...formData, [id]: value };

    if (id === "district") {
      const sel = districts.find((d) => d.districtCode.toString() === value);
      if (sel) {
        updated.district = sel.districtCode;
        updated.lgdCode = sel.districtCode;
        fetchBlocks(Number(value));
      }
    } else if (id === "bdoOfficeName") {
      const sel = blocks.find((b) => b.blockName === value);
      if (sel) { updated.bdoOfficeName = sel.blockName; updated.bdoOfficeCode = sel.blockCode; fetchVillages(sel.blockCode); }
    } else if (id === "villageName") {
      const sel = villages.find((v) => v.villageName === value);
      if (sel) {
        updated.villageName = sel.villageName; updated.villageCode = sel.villageCode;
        if (sel.talukName && sel.talukCode) { updated.thaluk = sel.talukName; updated.thalukCode = sel.talukCode; }
        else { updated.thaluk = ""; updated.thalukCode = ""; }
        fetchPinCode(sel.villageName);
      }
    }
    setFormData(updated);
    if (errors[id]) setErrors((prev) => { const n = { ...prev }; delete n[id]; return n; });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const Config = Authorization();
      const payload = { ...formData, jfmcTypeId: selectedJfmcType?._id };
      const response = await fetch(`${Api}/api/vfcs`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...Config.headers },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        toast.success("JFMC added successfully!");
        setFormData(initialStage);
        setErrors({});
      } else {
        toast.error("Error submitting form");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Error submitting form");
    } finally {
      setSubmitting(false);
    }
  };

  /* ════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════ */
  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {/* ── Page Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>Add JFMC</h4>
        <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
          <span style={{ color: "#A0AEC0" }}>JFMC</span>
          <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>Add JFMC</span>
        </div>
      </div>

      {/* ── No JFMC type selected warning ── */}
      {!selectedJfmcType && (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", padding: "32px 24px", textAlign: "center" }}>
          <div style={{ width: 52, height: 52, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
            <i className="mdi mdi-tag-outline" style={{ fontSize: 24, color: "#E53E3E" }} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>JFMC Type not selected</div>
          <div style={{ fontSize: 12, color: "#A0AEC0" }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar to choose a type before adding a JFMC.</div>
        </div>
      )}

      {/* ── Form Card ── */}
      {selectedJfmcType && (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>

          {/* Card header */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", borderBottom: "1px solid #EDF2F7", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
            <span style={{ width: 36, height: 36, background: "rgba(255,255,255,0.15)", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 18 }}>
              <i className="mdi mdi-home-plus-outline" />
            </span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>JFMC Registration Form</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>Fields marked * are required</div>
            </div>
          </div>

          {/* Form body */}
          <form onSubmit={handleSubmit} style={{ padding: "24px" }}>

            {/* ── Section: Basic Info ── */}
            <Section icon="mdi-home-group" title="Basic Information">
              <Field label={<>JFMC Name <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.vfcName}>
                <Input id="vfcName" placeholder="Enter JFMC Name" value={formData.vfcName} onChange={handleChange} error={errors.vfcName} />
              </Field>
              <Field label={<>Range <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.rangeId}>
                <Select id="rangeId" value={formData.rangeId} onChange={handleRangeChange} error={errors.rangeId}>
                  <option value="">Select Range</option>
                  {rangeOptions.sort((a, b) => a.rangeName.localeCompare(b.rangeName)).map((r) => (
                    <option key={r._id} value={r._id}>{r.rangeName}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Beat Name">
                <Select id="beatName" value={formData.beatName} onChange={handleBeatNameChange}>
                  <option value="">Select Beat</option>
                  {beatOptions.sort((a, b) => a.beatName.localeCompare(b.beatName)).map((b) => (
                    <option key={b._id} value={b.beatName}>{b.beatName}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Beat Code">
                <Input id="beatCode" value={formData.beatCode} onChange={handleChange} readOnly />
              </Field>
            </Section>

            {/* ── Section: Location ── */}
            <Section icon="mdi-map-marker-outline" title="Location Details">
              <Field label={<>District <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.district}>
                <Select id="district" value={formData.district} onChange={handleChange} error={errors.district}>
                  <option value="">Select District</option>
                  {districts.sort((a, b) => a.districtName.localeCompare(b.districtName)).map((d) => (
                    <option key={d._id} value={d.districtCode}>{d.districtName}</option>
                  ))}
                </Select>
              </Field>
              <Field label="LGD Code">
                <Input id="lgdCode" value={formData.lgdCode} onChange={handleChange} readOnly />
              </Field>
              <Field label="BDO Office Name">
                <Select id="bdoOfficeName" value={formData.bdoOfficeName} onChange={handleChange}>
                  <option value="">Select Block</option>
                  {Array.isArray(blocks) && blocks.slice().sort((a, b) => a.blockName.localeCompare(b.blockName)).map((b) => (
                    <option key={b.blockCode} value={b.blockName}>{b.blockName}</option>
                  ))}
                </Select>
              </Field>
              <Field label="BDO Office Code">
                <Input id="bdoOfficeCode" value={formData.bdoOfficeCode} onChange={handleChange} readOnly />
              </Field>
              <Field label={<>Village Name <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.villageName}>
                <Select id="villageName" value={formData.villageName} onChange={handleChange} error={errors.villageName}>
                  <option value="">Select Village</option>
                  {Array.isArray(villages) && villages.slice().sort((a, b) => a.villageName.localeCompare(b.villageName)).map((v) => (
                    <option key={v._id} value={v.villageName}>{v.villageName}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Village Code">
                <Input id="villageCode" value={formData.villageCode} onChange={handleChange} readOnly />
              </Field>
              <Field label={<>Location <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.location}>
                <Input id="location" placeholder="Enter Location" value={formData.location} onChange={handleChange} error={errors.location} />
              </Field>
              <Field label="Address">
                <Input id="address" placeholder="Enter Address" value={formData.address} onChange={handleChange} />
              </Field>
              <Field label={<>Panchayath <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.panchayath}>
                <Input id="panchayath" placeholder="Enter Panchayath" value={formData.panchayath} onChange={handleChange} error={errors.panchayath} />
              </Field>
              <Field label={<>Pin Code <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.pinCode}>
                <Input id="pinCode" type="number" placeholder="Auto-loaded" value={formData.pinCode} onChange={handleChange} error={errors.pinCode} />
              </Field>
              <Field label={<>Thaluk <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.thaluk}>
                <Input id="thaluk" placeholder="Enter Thaluk" value={formData.thaluk} onChange={handleChange} error={errors.thaluk} />
              </Field>
              <Field label={<>Thaluk Code <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.thalukCode}>
                <Input id="thalukCode" placeholder="Enter Thaluk Code" value={formData.thalukCode} onChange={handleChange} error={errors.thalukCode} />
              </Field>
            </Section>

            {/* ── Section: Registration ── */}
            <Section icon="mdi-file-certificate-outline" title="Registration Details">
              <Field label={<>Date of Establishment <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.dateOfEstablishment}>
                <Input id="dateOfEstablishment" type="date" value={formData.dateOfEstablishment} onChange={handleChange} error={errors.dateOfEstablishment} />
              </Field>
              <Field label={<>Registration Number <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.registrationNo}>
                <Input id="registrationNo" placeholder="Enter Registration Number" value={formData.registrationNo} onChange={handleChange} error={errors.registrationNo} />
              </Field>
              <Field label={<>Registration Date <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.registrationDate}>
                <Input id="registrationDate" type="date" value={formData.registrationDate} onChange={handleChange} error={errors.registrationDate} />
              </Field>
              <div />
              <Field label={<>Total Family <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.totalFamily}>
                <Input id="totalFamily" placeholder="Enter Total Family" value={formData.totalFamily} onChange={handleChange} error={errors.totalFamily} />
              </Field>
              <Field label={<>Total Population <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.totalPopulation}>
                <Input id="totalPopulation" placeholder="Enter Total Population" value={formData.totalPopulation} onChange={handleChange} error={errors.totalPopulation} />
              </Field>
            </Section>

            {/* ── Section: Bank ── */}
            <Section icon="mdi-bank-outline" title="Bank Details">
              <Field label={<>Bank Name <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.bankName}>
                <Input id="bankName" placeholder="Enter Bank Name" value={formData.bankName} onChange={handleChange} error={errors.bankName} />
              </Field>
              <Field label={<>Branch <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.branch}>
                <Input id="branch" placeholder="Enter Branch" value={formData.branch} onChange={handleChange} error={errors.branch} />
              </Field>
              <Field label={<>Account Number <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.accountNumber}>
                <Input id="accountNumber" placeholder="Enter Account Number" value={formData.accountNumber} onChange={handleChange} error={errors.accountNumber} />
              </Field>
              <Field label={<>IFSC Code <span style={{ color: "#E53E3E" }}>*</span></>} error={errors.ifscCode}>
                <Input id="ifscCode" placeholder="Enter IFSC Code" value={formData.ifscCode} onChange={handleChange} error={errors.ifscCode} />
              </Field>
            </Section>

            {/* ── Actions ── */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, paddingTop: 8, borderTop: "1px solid #EDF2F7", marginTop: 8 }}>
              <button
                type="submit"
                disabled={submitting}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "10px 28px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)",
                  color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700,
                  cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.7 : 1,
                  boxShadow: "0 3px 10px rgba(46,125,50,0.3)", transition: "all 0.2s",
                }}
              >
                <i className={`mdi ${submitting ? "mdi-loading mdi-spin" : "mdi-check-circle-outline"}`} />
                {submitting ? "Saving…" : "Save JFMC"}
              </button>
              <button
                type="button"
                onClick={() => { setFormData(initialStage); setErrors({}); }}
                style={{
                  padding: "10px 20px", background: "#fff", color: "#4A5568",
                  border: "1.5px solid #E2E8F0", borderRadius: 10, fontSize: 13,
                  fontWeight: 600, cursor: "pointer",
                }}
              >
                Reset
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AddVfc;
